import { fetchDomesticMatchesById, fetchMatchesById } from '../apis/matches';

import EventBusInstance from '@core/eventbus';
import LivePollingInstance from '@live/pollingHandler';
import type { LiveMatch, Match } from '@shared-types/match.types';
import { IS_TEST, IS_SIMULATION } from '@utilities/browser';
import { formatUrl } from '@utilities/fetch';
import { cleanupDomesticMatch, isFullMatch, getRefreshRate } from '@utilities/live';
import { MemoryCache } from '@utilities/memory-cache';
import { debounce } from '@utilities/throttle';
import { matchApiUrl, apiKey } from '@utilities/WindowContext';

export type MatchUnitServiceOptions = {
  match?: LiveMatch;
  includePlayerEvents?: boolean;
};

export class MatchUnitService {
  cache = new MemoryCache<string, LiveMatch>();
  livescoreUrls = new MemoryCache<number, string[]>();

  includePlayerEvents?: boolean;

  on({ match, includePlayerEvents }: MatchUnitServiceOptions, callback: (match: Match) => unknown, context: any) {
    if (!match?.id) return;

    match = cleanupDomesticMatch(match);

    if (!this.cache.has(match.id)) {
      this.cache.set(match.id, match);
    }

    this.includePlayerEvents ||= includePlayerEvents;

    EventBusInstance.on(`match_${match.id}_updated`, callback, context);
    this.start();

    const cachedMatch = this.cache.get(match.id);
    if (isFullMatch(cachedMatch)) {
      EventBusInstance.dispatch(`match_${match.id}_updated`, cachedMatch);
    }
  }

  getIntervals = () => {
    const matches = Array.from(this.cache.values()).filter(
      (match) => match.competition && match.competition?.region?.toUpperCase() !== 'DOMESTIC',
    );

    const intervalByCompetition = matches.reduce(
      (acc, match) => {
        const { id } = match.competition ?? {};
        const interval = getRefreshRate(match);

        if (!id || interval <= 0) return acc;

        acc[id] = Math.min(acc[id] ?? Infinity, interval);
        return acc;
      },
      {} as Record<string, number>,
    );

    return Object.entries(intervalByCompetition).reduce(
      (acc, [id, interval]) => {
        acc[interval] ||= [];
        acc[interval].push(id);
        return acc;
      },
      {} as Record<number, string[]>,
    );
  };

  getLivescoreUrl(competitionIds: string[]) {
    return formatUrl(matchApiUrl, 'v5/livescore', {
      competitionId: competitionIds,
      optionalFields: this.includePlayerEvents ? 'PLAYER_EVENTS' : '',
    });
  }

  start = debounce(async () => {
    await this.getMissingMatches();

    const intervals = this.getIntervals();

    Object.entries(intervals).forEach(([interval, ids]) => {
      if (+interval <= 0 || !ids.length) return;

      const newLivescoreUrl = this.getLivescoreUrl(ids);
      if (newLivescoreUrl !== this.livescoreUrls[interval]) {
        this.livescoreUrls[interval] && LivePollingInstance.off(this.livescoreUrls[interval], this.handler, this);
        this.livescoreUrls[interval] = newLivescoreUrl;
      }

      LivePollingInstance.on(
        this.livescoreUrls[interval],
        [{ key: 'x-api-key', value: apiKey }],
        +interval,
        this.handler,
        this,
      );
    });
  }, 250);

  getMissingMatches = async (domestic = false) => {
    if (IS_TEST && !IS_SIMULATION) return;

    if (!domestic) {
      await this.getMissingMatches(true);
    }

    const emptyIds = Array.from(this.cache)
      .filter(([, match]) => domestic === (match.competition?.region?.toUpperCase() === 'DOMESTIC'))
      .filter(([, match]) => IS_SIMULATION || !isFullMatch(match))
      .map(([id]) => id)
      .toSorted((a, b) => a.localeCompare(b));

    if (!emptyIds.length) return;
    const matches = (await (domestic ? fetchDomesticMatchesById(emptyIds) : fetchMatchesById(emptyIds)))?.map(
      (m) => ({ ...m, loaded: true }) as LiveMatch,
    );

    this.handler(matches);
  };

  handler = (matches: LiveMatch[]) => {
    matches?.forEach((match) => {
      match = cleanupDomesticMatch(match);
      match.streamUrl ??= null;
      const { id, hash } = match;
      let cachedMatch = this.cache.get(id);
      if (!IS_SIMULATION && !IS_TEST && hash && hash === cachedMatch?.hash) return;

      cachedMatch = {
        ...cachedMatch,
        ...match,
      };

      if (!isFullMatch(cachedMatch)) {
        this.getMissingMatches();
        return;
      }
      this.cache.set(id, cachedMatch);

      EventBusInstance.dispatch(`match_${id}_updated`, cachedMatch);
    });
  };

  off({ match }: MatchUnitServiceOptions, callback: (match: Match) => unknown, context: any) {
    if (!match?.id) return;

    EventBusInstance.off(`match_${match.id}_updated`, callback, context);

    if (EventBusInstance.anybody(`match_${match.id}_updated`)) return;

    this.cache.delete(match.id);

    this.start();
  }

  static readonly instance = new MatchUnitService();
}

const matchUnitServiceInstance = MatchUnitService.instance;
export default matchUnitServiceInstance;
